import { Inject, Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ACLService } from '@delon/acl';
import { DA_SERVICE_TOKEN, ITokenService } from '@delon/auth';
import { _HttpClient, MenuService, SettingsService } from '@delon/theme';
import { environment } from '@env/environment';
import { NzSafeAny } from 'ng-zorro-antd/core/types';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Subject } from 'rxjs';
import { AuthService } from 'src/app/core/util/auth.service';
import { v4 as uuidv4 } from 'uuid'; // Import the uuid function

import { BuildService } from './build.service';

@Injectable()
export class AppService implements OnDestroy {
  private interval$: NzSafeAny;
  private currentVersion = '';
  currentTid = '';
  private currentJwt: any = {};
  destroy$ = new Subject<void>();
  constructor(
    private modalSrv: NzModalService,
    private router: Router,
    private http: _HttpClient,
    private auth: AuthService,
    @Inject(DA_SERVICE_TOKEN) private tokenService: ITokenService,
    private settingsService: SettingsService,
    private buildService: BuildService,
    private aclService: ACLService,
    private menuService: MenuService
  ) {
    this.router.events.subscribe(ev => {
      if (ev instanceof NavigationEnd) {
        // When the route is changed, reset the version check timer
        this.startVersionCheck();
      }
    });
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public getTerminalId() {
    let terminalId = localStorage.getItem('terminalId');
    if (terminalId) return terminalId;

    terminalId = uuidv4();
    localStorage.setItem('terminalId', terminalId);
    return terminalId;
  }

  public getTerminalName() {
    const userAgent = navigator.userAgent.toLowerCase();

    if (userAgent.includes('firefox')) {
      return 'Firefox';
    } else if (userAgent.includes('edg')) {
      return 'Edge';
    } else if (userAgent.includes('opr') || userAgent.includes('opera')) {
      return 'Opera';
    } else if (userAgent.includes('chrome')) {
      return 'Chrome';
    } else if (userAgent.includes('safari')) {
      return 'Safari';
    } else {
      return 'Unknown Browser';
    }
  }

  showNewVersionAlert() {
    this.modalSrv.confirm({
      nzTitle: `New Version Detected`,
      nzContent: `A new version of the app may have been released, please click Reload button to take effect.`,
      nzCancelDisabled: false,
      nzOkText: 'Reload',
      nzCancelText: 'Later',
      nzOnOk: () => this.reload(true)
    });
  }

  reload(force: boolean) {
    if (force) {
      this.muteLeaveAppAlert();
    }
    window.location.reload();
  }

  muteLeaveAppAlert() {
    if (!window.app) window.app = {};
    window.app.forcereload = true;
  }

  forceReload() {
    this.reload(true);
  }

  forceReloadAndReturnHome() {
    this.muteLeaveAppAlert();

    this.router.navigateByUrl('/dashboard').then(() => {
      this.muteLeaveAppAlert();
      window.location.reload();
    });
  }

  forceReloadAndToPlan(tid: any) {
    this.muteLeaveAppAlert();

    this.router
      .navigate([`/account/subscription/subscribe/${tid}`], {
        queryParams: { mode: 'trial' }
      })
      .then(() => {
        this.muteLeaveAppAlert();
        window.location.reload();
      });
  }

  public startVersionCheck(frequency = 1000 * 60 * 60) {
    if (this.interval$) clearInterval(this.interval$);

    this.interval$ = setInterval(() => {
      this.checkVersion();
    }, frequency);
  }

  private checkVersion() {
    // Get local version from BuildService
    this.buildService.getBuildNumber().subscribe((localVersion: string) => {
      // Fetch remote version from server
      this.http
        .get(`assets/build.json?_silent=true&t=${new Date().getTime()}`)
        .subscribe({
          next: (response: any) => {
            const remoteVersion = response.build;
            const hashChanged = this.hasHashChanged(remoteVersion);
            // If new version, do something
            if (hashChanged) {
              if (this.interval$) clearInterval(this.interval$);
              this.forceReload();
              // this.showNewVersionAlert();
            }
          },
          error: error => {
            console.warn('Could not fetch remote version:', error);
            // Fallback: compare with local version
            const hashChanged = this.hasHashChanged(localVersion);
            if (hashChanged) {
              if (this.interval$) clearInterval(this.interval$);
              this.forceReload();
            }
          }
        });
    });
  }

  private hasHashChanged(newVersion: string) {
    if (!this.currentVersion) {
      this.currentVersion = newVersion;
      return false;
    }

    return this.currentVersion !== newVersion;
  }

  // Check user token every 60 minutes
  public startCheckAlive(frequency = 1000 * 60 * 60) {
    setInterval(() => {
      this.ping();
    }, frequency);
  }

  private ping() {
    this.http.post('api/user/ping', {}).subscribe({
      next: () => {},
      error: () => {
        // this.tokenService.clear();
        // this.router.navigateByUrl(this.tokenService.login_url);
      }
    });
  }

  public startListenSwitchAccount(): void {
    const token = this.tokenService.get();
    this.currentJwt = { ...token };
    this.stopListenStorage();
    if (window?.addEventListener)
      window.addEventListener('storage', this.storageEventListener.bind(this));
  }

  private storageEventListener(event: StorageEvent) {
    if (
      event.storageArea == localStorage &&
      event.key === this.tokenService.options.store_key &&
      event.newValue
    ) {
      try {
        const jwt = JSON.parse(event.newValue);
        if (
          this.currentJwt &&
          (this.currentJwt.tenantId != jwt.tenantId ||
            this.currentJwt.email != jwt.email)
        ) {
          this.stopListenStorage();
          this.currentJwt = jwt;
          this.promptToReload();
        }
      } catch (e) {
        console.error('Error occurs on storageEventListener');
        console.error(e);
      }
    }
  }

  private stopListenStorage(): void {
    window.removeEventListener('storage', this.storageEventListener.bind(this));
  }

  private promptToReload() {
    const modal = this.modalSrv.create({
      nzTitle: 'Back to Dashboard',
      nzClosable: false,
      nzMaskClosable: false,
      nzContent:
        'You have signed in another account/company, click Reload button to return to dashboard.',
      nzFooter: [
        {
          label: 'Reload',
          type: 'primary',
          onClick: () => {
            modal.destroy();
            this.forceReloadAndReturnHome();
          }
        }
      ]
    });
  }

  get reportServerUrl(): string {
    let serverUrl = environment.api['reporting'];
    // remove ending slash
    if (serverUrl.endsWith('/')) serverUrl = serverUrl.slice(0, -1);
    return serverUrl;
  }

  /**
   * Refreshes ACL abilities and menu from backend
   * Useful when abilities or menu items need to be updated dynamically
   */
  refreshMenuAndAcl(): void {
    // Call the backend API to get updated ACL and menu
    const terminalId = this.getTerminalId();
    const terminalName = this.getTerminalName();

    this.http.get(`api/app/qca`, { terminalId, terminalName }).subscribe({
      next: (res: any) => {
        const acl = res.acl;

        // Update user information with new ACL
        const currentUser = this.settingsService.user || {};
        const dateFormat = res.user?.dateFormat ?? currentUser['dateFormat'];
        this.settingsService.setUser({ ...currentUser, acl: acl, dateFormat: dateFormat });

        // Update ACL abilities
        this.aclService.setFull(acl.full);
        this.aclService.setAbility(acl.abilities);

        // Update menu from backend
        const menu = res.menu ?? [];
        this.menuService.add(menu);
        this.menuService.resume();
      },
      error: err => {
        console.error('Failed to refresh menu and ACL:', err);
      }
    });
  }
}
